<template>
  <i-modal
    dontSave
    max-width="95vw"
    persistent
    :load="loading"
    :title="suborder.name"
    v-model="dialog"
  >
    <v-row class="my-0">
      <!-- Imagen -->
      <v-col cols="12" md="3">
        <div class="d-flex flex-column align-center justify-center h-100 px-3">
          <img
            class="producerModal--image"
            :src="getProductImage(suborder.thumbnail)"
            :alt="suborder.name"
            @click="openImage(getProductImage(suborder.thumbnail))"
          />
          <small v-if="suborder.thumbnail" class="text--muted">
            {{ $t('click_to_enlarge') }}
          </small>
        </div>
      </v-col>

      <!-- Estados, nombre, fechas, orden, suborden y descripción -->
      <v-col cols="12" md="9">
        <v-alert
          v-if="!suborder.orderProductDepartment.length"
          type="error"
          outlined
        >
          {{ $t('assignProductions') }}
        </v-alert>
        <!-- Estados -->
        <set-suborder-state
          v-if="
            suborder.template &&
            stages.length > 0 &&
            suborder.orderProductDepartment.length
          "
          :states="stages"
          @submitAndClose="submit()"
          v-model="suborder.status"
        />

        <!-- Nombre y fechas -->
        <div class="producerModal--container cols-1-1-1">
          <!-- Nombre de la empresa o del cliente -->
          <v-text-field
            class="producerModal--input"
            color="secondary"
            type="text"
            outlined
            disabled
            :label="
              suborder.clientName ? $tc('company_name', 1) : $tc('customer', 1)
            "
            :value="
              suborder.clientName
                ? suborder.clientName
                : suborder.clientFullname
            "
          ></v-text-field>

          <!-- Fecha de la orden -->
          <v-text-field
            class="producerModal--input"
            color="secondary"
            type="text"
            outlined
            disabled
            :label="$tc('dateOrder', 1)"
            :value="dateFormat(suborder.orderDate)"
          ></v-text-field>

          <!-- Fecha de compromiso -->
          <v-text-field
            class="producerModal--input"
            color="secondary"
            type="text"
            outlined
            disabled
            :label="$tc('promise_date', 1)"
            :value="dateFormat(suborder.promiseDate)"
          ></v-text-field>
        </div>

        <!-- Orden, suborden y descripción -->
        <div class="producerModal--container cols-1-2">
          <div>
            <!-- Orden -->
            <v-text-field
              class="producerModal--input"
              color="secondary"
              type="text"
              outlined
              disabled
              :label="$tc('project_name', 1)"
              :value="suborder.projectName"
            ></v-text-field>

            <!-- Suborden -->
            <v-text-field
              class="producerModal--input"
              color="secondary"
              type="text"
              outlined
              disabled
              :label="$tc('suborder', 1)"
              :value="suborder.name"
            ></v-text-field>
          </div>

          <!-- Descripción -->
          <v-textarea
            class="producerModal--input"
            color="secondary"
            outlined
            disabled
            :label="$tc('description', 1)"
            :value="suborder.description"
          />
        </div>
      </v-col>
    </v-row>

    <!-- Producto -->
    <h2 class="px-3 mt-3">
      {{ `${$tc('product', 1)}:  ${suborder.own_prod}` }}
    </h2>

    <!-- Base -->
    <v-row v-if="suborder.price_type === 'base'" class="my-0 px-3">
      <v-col cols="12" md="3">
        <v-text-field
          class="producerModal--input"
          color="secondary"
          type="text"
          outlined
          disabled
          :label="$tc('quantity', 1)"
          :value="suborder.quantity"
        ></v-text-field>
      </v-col>
    </v-row>

    <!-- Fórmula -->
    <v-row v-if="suborder.price_type === 'formula'" class="my-0 px-3">
      <v-col cols="12" md="4" lg="3">
        <v-text-field
          class="producerModal--input"
          color="secondary"
          type="text"
          outlined
          disabled
          :label="$tc('quantity', 1)"
          :value="suborder.quantity"
        ></v-text-field>
      </v-col>
      <v-col
        v-for="(item, index) in Object.values(suborder.dimensions)"
        :key="`formula-${index}`"
        cols="12"
        md="4"
        lg="3"
      >
        <v-text-field
          class="producerModal--input"
          color="secondary"
          type="text"
          disabled
          outlined
          :label="item.name"
          :suffix="item.measure"
          :value="item.value"
        ></v-text-field>
      </v-col>
    </v-row>

    <!-- Matriz -->
    <v-row v-if="suborder.price_type === 'matrix'" class="my-0 px-3">
      <v-col class="pt-0" cols="12">
        <i-matrix
          class="producerModal--input p-0"
          hideSubtotal
          :subOrder="suborder"
          :type_customer="suborder.level"
          :productId="suborder.product"
          :priceString="suborder.priceString"
          :price="suborder.price"
          :qty="suborder.quantity"
          :matrixUse="'product'"
          :canViewPrice="false"
        ></i-matrix>
      </v-col>
    </v-row>

    <!-- Detalles de producción -->
    <div v-if="productionDetails && productionDetails.length" class="px-3">
      <h2>{{ $tc('details_production') }}</h2>
      <div
        v-for="(item, index) in productionDetails"
        :key="`fields-${index}`"
        class="mt-3 pt-3 px-3 producerModal--detail rounded-lg"
      >
        <ul class="mb-0">
          <li class="h4">{{ `${$tc('detail', 1)}:  ${item.name}` }}</li>
        </ul>
        <v-row
          v-if="item.price_type === 'base' || item.price_type === 'bool'"
          class="my-0"
        >
          <!-- Cantidad -->
          <v-col cols="12" md="4" lg="3">
            <v-text-field
              class="producerModal--input"
              color="secondary"
              type="text"
              outlined
              disabled
              :label="$tc('quantity', 1)"
              :value="item.is_multipliable ? suborder.quantity : 1"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row v-if="item.price_type === 'formula'" class="my-0">
          <!-- Cantidad -->
          <v-col cols="12" md="4" lg="3">
            <v-text-field
              class="producerModal--input"
              color="secondary"
              type="text"
              outlined
              disabled
              :label="$tc('quantity', 1)"
              :value="item.is_multipliable ? suborder.quantity : 1"
            ></v-text-field>
          </v-col>

          <!-- Dimensiones -->
          <v-col
            v-for="(dimension, index) in Object.values(item.dimensions)"
            :key="`ddp-formula-${index}`"
            cols="12"
            md="4"
            lg="3"
          >
            <v-text-field
              class="producerModal--input"
              color="secondary"
              type="text"
              disabled
              outlined
              :label="dimension.name"
              :suffix="dimension.measure"
              :value="dimension.value"
            ></v-text-field>
          </v-col>
        </v-row>
        <div v-if="item.price_type === 'matrix'" class="my-0">
          <i-matrix
            class="producerModal--input p-0"
            hideSubtotal
            :canViewPrice="false"
            :detail="item"
            :main_matrix="suborder.main_matrix"
            :matrixUse="'details'"
            :price="item.price"
            :priceString="item.priceString"
            :productId="suborder.product"
            :qty="item.is_multipliable ? suborder.quantity : 1"
            :detailId="item.detail || item.pk"
            :subdetailId="item.subdetail || null"
            :subOrder="suborder"
            :type_customer="suborder.level"
          ></i-matrix>
        </div>
      </div>
    </div>
  </i-modal>
</template>

<script>
import SetSuborderState from './SetSuborderState.vue'

export default {
  components: {
    SetSuborderState
  },
  props: {},
  data() {
    return {
      dialog: false,
      loading: false,
      productionDetails: '',
      stages: [],
      suborder: {
        thumbnail: '',
        orderDate: '',
        promiseDate: '',
        clientName: '',
        clientFullname: '',
        projectName: '',
        description: '',
        name: '',
        own_prod: '',
        quantity: '',
        orderProductDepartment: []
      }
    }
  },
  methods: {
    /**
     * Método para abrir la modal y consultar la suborden respectiva
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    async open(suborderPK) {
      this.dialog = true
      try {
        this.loading = true
        let response = await this.$api.sale.suborder.show({
          pk: suborderPK
        })
        this.getSavedItems(suborderPK)
        this.suborder = response.data
        this.getsubOrderStatus()
      } catch (error) {
        console.error(error)
        this.dialog = false
      } finally {
        this.loading = false
      }
    },
    /**
     * Método que muestra la imagen de la orden en caso que la tenga, en caso
     * contrario mostrará el placeholder
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    getProductImage(URLImage) {
      if (URLImage) {
        return URLImage
      }
      return require('@/assets/default_image.png')
    },
    /**
     * Método para consultar los detalles de producción asociados a una suborden
     * de compra.
     *
     * @param {Number} suborderPK ID de la suborden
     * @returns {Void}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    async getSavedItems(suborderPK) {
      let response = await this.$api.suborder.production.show({
        pk: suborderPK,
        opt: {
          params: {
            subOrder: true
          }
        }
      })
      this.productionDetails = response.data
    },
    /**
     * Método para consultar los subestados de suborden, según su estado en la
     * plantilla de proceso de producción asociada al producto.
     *
     * @author Miguel E. Villamizar R. <mevr02 at gmail.com>
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getsubOrderStatus() {
      let response = await this.$api.sale.status.list({
        opt: {
          params: {
            template: this.suborder.template
          }
        }
      })
      let sta = []
      for (var value in response.data) {
        if (response.data[value].substatusVals.length > 0) {
          sta = sta.concat(response.data[value].substatusVals)
        } else {
          sta.push(response.data[value])
        }
      }
      let root_filter = sta.filter(
        (element) => element.pk === this.suborder.status
      )
      if (root_filter.length > 0) {
        const {
          dad: { name },
          root
        } = root_filter[0]
        if (name !== 'preprocess') {
          this.stages = sta.filter((element) => element.root === root)
        }
      }
    },
    /**
     * Método para recolectar la información a guardar de una subOrden,
     * una vez verificado que tenga usuarios asignados y se realice la
     * notificación en caso de cambio de estado
     *
     * @author Miguel E. Villamizar R. <mevr02 at gmail.com>
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async submit() {
      let form_data = new FormData()
      for (var key in this.suborder) {
        if (this.suborder[key] == null) {
          continue
        }
        form_data.append(
          key,
          key !== 'dimensions'
            ? this.suborder[key]
            : JSON.stringify(this.suborder[key])
        )
        if (
          key == 'thumbnail' &&
          (this.suborder.thumbnail == null ||
            String(this.suborder.thumbnail).indexOf(';base64') != -1 ||
            String(this.suborder.thumbnail).indexOf('http') != -1)
        ) {
          form_data.delete('thumbnail')
        }
      }
      try {
        this.load = true
        this.save(form_data)
      } finally {
        // Logica para enviar la señal a sale/index para que se actualice.
        this.componentKey += 1
        localStorage.setItem('componentKey', this.componentKey)
      }
    },
    /**
     * Método para guardar o editar una suborden
     *
     * @author Paul Rojas
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async save(form_data) {
      let response = await this.$api.sale.suborder.edit({
        pk: this.suborder.pk,
        form: form_data
      })
      this.dialog = false
      this.$toast.success(`${this.$tc('suborder', 1)} ${this.$tc('edited', 2)}`)

      if (response.data.emailTemplate) {
        this.$store.dispatch(
          'utils/openDialogEmailNotification',
          response.data.emailTemplate
        )
      }

      this.$emit('prod')
    },
    /**
     * Método para abrir la modal y consultar la suborden respectiva
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    openImage(imgURL) {
      window.open(imgURL)
    }
  }
}
</script>
<style lang="sass">
.producerModal--image
  height: auto
  max-width: 250px
  width: 100%
  border-radius: 8px
.producerModal--container
  display: flex
  gap: 20px
  padding: 8px 12px
  &.cols-1-2
    div:nth-child(1)
      flex: 1
    div:nth-child(2)
      flex: 2
  &.cols-1-1-1
    flex: 0 0 33.3333333333%
.producerModal--input
  .v-text-field__slot, .v-input__slot
    input, textarea, .v-select__slot
      font-size: 24px
    label
      font-size: 18px

.theme--light
  .producerModal--detail
    background-color: rgb(170 170 170 / 0.14)
    box-shadow: 0px 1px 1px -2px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 1px 0px rgba(0, 0, 0, 0.12)
.theme--dark
  .producerModal--detail
    border: 1px solid rgba(132, 132, 132, 0.29)
</style>
